import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { setReferralCode } from '@/lib/referral';
import { referralCodeFromStartParam } from '@/lib/startParam';
import { whenFirstScreenSettled } from './firstScreen';

type ThemeParams = {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  destructive_text_color?: string;
};

type SafeArea = { top: number; right: number; bottom: number; left: number };

type WebApp = {
  ready: () => void;
  expand: () => void;
  close: () => void;
  initData?: string;
  initDataUnsafe?: {
    user?: { id: number; first_name?: string; username?: string };
    start_param?: string;
  };
  colorScheme?: 'light' | 'dark';
  themeParams?: ThemeParams;
  safeAreaInset?: SafeArea;
  contentSafeAreaInset?: SafeArea;
  onEvent?: (e: string, cb: () => void) => void;
  offEvent?: (e: string, cb: () => void) => void;
  HapticFeedback?: { impactOccurred: (style: 'light' | 'medium' | 'heavy') => void };
  MainButton?: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (t: string) => void;
    setParams: (p: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  BackButton?: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  // Opens a URL in Telegram's in-app browser as a first-party top-level
  // context; the Mini App stays open underneath. Used to launch cookie-session
  // vendor games (h99) that a cross-site iframe can't run on iOS WebKit.
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  // Follows a t.me link inside the Telegram client itself. The right call for
  // every Telegram destination: window.open hands the URL to the OS deep-link
  // handler instead, which asks the user to confirm reopening Telegram and
  // relaunches the Mini App. See lib/openExternal.
  openTelegramLink?: (url: string) => void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
    // Resolves once the async Telegram SDK script has run — or failed to.
    // Declared in index.html; see the detection poll for why it matters.
    __tgSdkSettled?: Promise<unknown>;
  }
}

type TwaUser = { id: number; first_name?: string; username?: string };

// Telegram Web passes initData as a `tgWebAppData` URL hash param which
// main.tsx captures and stores in sessionStorage before our hash router
// overwrites the hash. Parse it here as a fallback when the SDK's own
// `tg.initData` is empty (e.g. Telegram Web client, older desktop builds).
function readCachedInitData(): string | null {
  try { return sessionStorage.getItem('hg.tg-init-data') || null; } catch { return null; }
}

// Some Telegram clients (native iOS/Android) do not populate
// initDataUnsafe.start_param even though the raw initData string contains
// `start_param=CODE`. Parse it directly as a reliable fallback.
function parseStartParamFromInitData(raw: string): string | null {
  try { return new URLSearchParams(raw).get('start_param'); } catch { return null; }
}

function getLiveInitData(): string | null {
  return window.Telegram?.WebApp?.initData || null;
}

function parseUserFromInitData(raw: string): TwaUser | null {
  try {
    const p = new URLSearchParams(raw);
    const u = p.get('user');
    if (!u) return null;
    const parsed = JSON.parse(u) as { id: number; first_name?: string; username?: string };
    if (typeof parsed.id !== 'number') return null;
    return { id: parsed.id, first_name: parsed.first_name, username: parsed.username };
  } catch { return null; }
}

type TwaContextValue = {
  ready: boolean;
  isTelegram: boolean;
  // True while the provider is still polling for the Telegram SDK payload on a
  // cold first open (initData arrives asynchronously). During this window
  // isTelegram is not yet trustworthy, so Telegram-vs-browser-gated UI (e.g.
  // the web-only "TG Quick Sign In" button) should wait rather than flash.
  detecting: boolean;
  webApp: WebApp | null;
  user: TwaUser | null;
  // Raw signed initData string — pass directly to the backend's
  // /api/v1/telegram/miniapp-auth endpoint. null outside Telegram.
  initData: string | null;
  // start_param from deep links (t.me/<bot>?startapp=<code>). Used as the
  // referral / invitation code for first-launch auto-register.
  startParam: string | null;
  colorScheme: 'light' | 'dark';
  safeArea: SafeArea;
  haptic: (style?: 'light' | 'medium' | 'heavy') => void;
  // Reads the SDK's live initData synchronously, bypassing React state. The
  // TWA init effect resolves `initData` into context asynchronously (it may
  // retry up to 600ms later), so a user who acts immediately — e.g. taps "TG
  // Quick Sign In" right after the mini-app opens from an external link —
  // can race ahead of that state update even though the SDK itself already
  // has the payload. Centralised here so pages never read `window.Telegram`
  // directly.
  getLiveInitData: () => string | null;
};

const defaultSafe: SafeArea = { top: 0, right: 0, bottom: 0, left: 0 };

const TwaContext = createContext<TwaContextValue>({
  ready: false,
  isTelegram: false,
  detecting: false,
  webApp: null,
  user: null,
  initData: null,
  startParam: null,
  colorScheme: 'light',
  safeArea: defaultSafe,
  haptic: () => {},
  getLiveInitData,
});

const FALLBACK_THEME: Required<ThemeParams> = {
  bg_color: '#f4f5f7',
  secondary_bg_color: '#ffffff',
  text_color: '#111111',
  hint_color: '#888888',
  link_color: '#1055ff',
  button_color: '#1055ff',
  button_text_color: '#ffffff',
  destructive_text_color: '#ff3b30',
};

function applyTheme(params: ThemeParams | undefined) {
  const merged = { ...FALLBACK_THEME, ...(params ?? {}) };
  const root = document.documentElement;
  root.style.setProperty('--color-app', merged.bg_color);
  root.style.setProperty('--color-surface', merged.secondary_bg_color);
  root.style.setProperty('--color-text', merged.text_color);
  root.style.setProperty('--color-muted', merged.hint_color);
  root.style.setProperty('--color-accent', merged.button_color);
  root.style.setProperty('--color-accent-text', merged.button_text_color);
  root.style.setProperty('--color-link', merged.link_color);
  root.style.setProperty('--color-danger', merged.destructive_text_color);
}

export function TwaProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<TwaContextValue>(() => {
    applyTheme(undefined);
    return {
      ready: false,
      isTelegram: false,
      detecting: false,
      webApp: null,
      user: null,
      initData: null,
      startParam: null,
      colorScheme: 'light',
      safeArea: defaultSafe,
      haptic: () => {},
      getLiveInitData,
    };
  });

  useEffect(() => {
    // Attempt to initialise the Telegram Mini App context.
    // Returns an event-listener cleanup fn when a valid session is detected,
    // or null when the SDK payload (initData / user / start_param) is not yet
    // available — which happens on the first open from an external link before
    // Telegram's SDK has finished its async initialisation.
    const tryInit = (): (() => void) | null => {
      // Read live on every attempt. `window.Telegram` is published by the
      // async SDK script, which on a cold first open can run AFTER this
      // effect; a reference captured once would stay undefined for the rest
      // of the session and the Mini App would never be detected at all.
      const tg = window.Telegram?.WebApp;
      if (!tg) return null;

      // Primary source: SDK-provided initData (populated in native Telegram
      // clients). Fallback: value captured from the URL hash by main.tsx
      // before the router overwrote it (Telegram Web / some desktop builds).
      const sdkInitData = tg.initData && tg.initData.length > 0 ? tg.initData : null;
      const initData = sdkInitData ?? readCachedInitData();

      // User: prefer SDK's parsed object; fall back to parsing the initData
      // string ourselves when initDataUnsafe.user is absent.
      const user = tg.initDataUnsafe?.user ?? (initData ? parseUserFromInitData(initData) : null);
      const isMiniAppSession = Boolean(initData || user || tg.initDataUnsafe?.start_param);

      // The Telegram SDK script is loaded on every page. In a regular browser
      // it can still define `window.Telegram.WebApp`, so the object existing
      // alone is not enough to prove this page is running inside Telegram.
      // Only treat the session as Telegram when real Mini App payload exists.
      if (!isMiniAppSession) return null;

      try {
        tg.expand();
      } catch {
        // ignore — older clients may not support this
      }
      // Deferred: ready() lifts Telegram's loading placeholder, so it waits
      // for Home's first screen (at most 1 s from page open) — see
      // ./firstScreen.ts. tryInit can run more than once; a repeated ready()
      // is a no-op for the client.
      void whenFirstScreenSettled().then(() => {
        try {
          tg.ready();
        } catch {
          // ignore — older clients may not support this
        }
      });
      // Keep the app's own theme fixed instead of inheriting Telegram's
      // light/dark palette. We still use the WebApp object for initData,
      // safe-area, haptics, and other Mini App integrations.
      applyTheme(undefined);

      // Read the current insets and publish them as CSS variables on
      // <html> so chrome components (HeaderBar / SubPageHeader / PageHeader)
      // can pad-top via `var(--tg-safe-top)` without re-rendering on every
      // event. Returns the SafeArea so callers can also stash it in React
      // state when they want a value-typed dependency.
      const readSafeArea = (): SafeArea => {
        // safeAreaInset  = device insets (status bar / notch / home indicator)
        // contentSafeAreaInset = Telegram's own system buttons (Close, options)
        //   that are drawn on top of the mini-app viewport.
        // Both must be ADDED — one ?? other loses whichever is non-null.
        const sa  = tg.safeAreaInset ?? defaultSafe;
        const csa = tg.contentSafeAreaInset ?? defaultSafe;
        const combined: SafeArea = {
          top:    sa.top    + csa.top,
          right:  sa.right  + csa.right,
          bottom: sa.bottom + csa.bottom,
          left:   sa.left   + csa.left,
        };
        const root = document.documentElement;
        root.style.setProperty('--tg-safe-top',    `${combined.top}px`);
        root.style.setProperty('--tg-safe-right',  `${combined.right}px`);
        root.style.setProperty('--tg-safe-bottom', `${combined.bottom}px`);
        root.style.setProperty('--tg-safe-left',   `${combined.left}px`);
        return combined;
      };

      // Prefer SDK's parsed object; fall back to parsing the raw initData
      // string (some native clients omit it from initDataUnsafe).
      const startParam =
        tg.initDataUnsafe?.start_param ??
        (initData ? parseStartParamFromInitData(initData) : null);

      // Keep the in-memory referral store in sync so getReferralCode() returns
      // startParam within this session (used by miniAppAuth invitation param).
      // A value naming a screen is a deep link, not a code, and must not reach
      // the backend as an invitation the user never had.
      const referral = referralCodeFromStartParam(startParam);
      if (referral) {
        setReferralCode(referral);
      }

      setValue({
        ready: true,
        isTelegram: true,
        detecting: false,
        webApp: tg,
        user,
        initData,
        startParam,
        colorScheme: 'light',
        safeArea: readSafeArea(),
        haptic: (style = 'light') => tg.HapticFeedback?.impactOccurred(style),
        getLiveInitData,
      });

      // Insets the mini-app starts with are stale when launched from the
      // chatlist "open" button (compact mode → expanded changes both the
      // viewport and the inset values). Subscribe to the post-mount events
      // so the top status bar / TG bar overlay never sits on top of our
      // chrome. Idempotent: refreshing the CSS vars + state on every event
      // is cheap.
      const refresh = () => {
        const sa = readSafeArea();
        setValue((prev) => (prev.isTelegram ? { ...prev, safeArea: sa } : prev));
      };
      const events = ['viewportChanged', 'safeAreaChanged', 'contentSafeAreaChanged'];
      try {
        events.forEach((e) => tg.onEvent?.(e, refresh));
      } catch {
        // older clients without these event names
      }
      return () => {
        try {
          events.forEach((e) => tg.offEvent?.(e, refresh));
        } catch {
          // older clients without offEvent
        }
      };
    };

    // First attempt — succeeds in native Telegram (initData is synchronous
    // there). A session up front does NOT finish the job: tryInit samples
    // start_param once, and several clients publish it a beat AFTER the
    // session. This used to return here, so on those clients start_param was
    // read as null and never looked at again — a `?startapp=` link signed the
    // user in and left them on Home, and only a reload (warm SDK, value ready
    // on the first read) took them where they asked to go. The poll below now
    // runs either way; when the session is already up it has only the one job.
    let listenerCleanup: (() => void) | null = tryInit();
    const sessionUpFront = listenerCleanup !== null;

    // Dev-only escape hatch: simulate the Telegram Mini App environment in a
    // regular browser so the login bottom-sheet (and any other TG-gated UI)
    // can be exercised. Toggle in DevTools:
    //   localStorage.setItem('hg_simulate_tg', '1')   // enable, reload
    //   localStorage.removeItem('hg_simulate_tg')     // disable, reload
    // Stripped out of production builds.
    if (
      !sessionUpFront &&
      import.meta.env.DEV &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('hg_simulate_tg') === '1'
    ) {
      setValue({
        ready: true,
        isTelegram: true,
        detecting: false,
        webApp: null,
        user: { id: 12324242, username: 'testuser', first_name: 'Test' },
        // Simulated env never has a real signed initData — the automatic
        // sign-in sees null and skips the API call, leaving the existing
        // browser auth flows intact.
        initData: null,
        startParam: null,
        colorScheme: 'light',
        safeArea: defaultSafe,
        haptic: () => {},
        getLiveInitData,
      });
      return;
    }

    // No Mini App session on the synchronous attempt. That is NOT the same as
    // "not Telegram": on Telegram Web and Desktop the payload — and sometimes
    // `window.Telegram` itself — arrives with the async SDK script, which on a
    // cold first open can run after React has mounted.
    //
    // This branch used to be entered only when `window.Telegram.WebApp`
    // already existed, so an SDK that landed late was never seen: detection
    // never started, `detecting` stayed false, and useTelegramAutoSignIn read
    // that as a settled "not Telegram" and abandoned the automatic sign-in for
    // the rest of the page's life. Polling now runs unconditionally.
    //
    // Mark the app ready so the UI renders, and hold `detecting` up until the
    // question is actually answered — Telegram-gated UI keys off it so it
    // doesn't act on the browser answer while the SDK is still in flight.
    if (!sessionUpFront) {
      setValue((current) => ({ ...current, ready: true, detecting: true }));
    }

    let startParamPublished = false;
    let sdkSettled = false;

    // Resolves when the SDK script has either run or failed — the signal that
    // separates "no Telegram here" from "not loaded yet". index.html publishes
    // it; the timer covers a stale cached index.html served from the previous
    // deploy, where the promise is absent and the only safe answer is to give
    // the SDK a moment before concluding anything.
    const settled: Promise<unknown> =
      window.__tgSdkSettled ?? new Promise((resolve) => setTimeout(resolve, 3000));
    void settled.then(() => { sdkSettled = true; });

    // Read start_param from whichever source currently has it. On Telegram
    // Desktop this can lag behind the session: the client populates
    // initDataUnsafe.user (and thus a "valid session") a beat BEFORE
    // start_param appears, so we must keep re-reading it after the session
    // is up rather than sampling it once.
    const readStartParam = (): string | null => {
      const tg = window.Telegram?.WebApp;
      if (!tg) return null;
      return (
        tg.initDataUnsafe?.start_param ??
        (tg.initData ? parseStartParamFromInitData(tg.initData) : null)
      );
    };

    // Two deadlines, because the two questions settle at different times.
    // `detecting` must clear promptly — browser visitors are waiting on it —
    // so it ends a short grace after the SDK settles, by which point a real
    // client has long since published its payload. The start_param hunt runs
    // on to a hard cap, since on Desktop the code can trail the session.
    const GRACE_AFTER_SDK_MS = 2000;
    const HARD_CAP_MS = 15000;
    const startedAt = Date.now();
    let sdkSettledAt: number | null = null;
    // A session up front has already answered the detection question; only the
    // start_param hunt is left.
    let detectionSettled = sessionUpFront;
    startParamPublished = Boolean(readStartParam());

    const poll = setInterval(() => {
      const now = Date.now();
      if (sdkSettled && sdkSettledAt === null) sdkSettledAt = now;

      // Establish the Mini App session (initData / user) once available.
      // tryInit() publishes isTelegram and clears detecting on success.
      if (!listenerCleanup) {
        listenerCleanup = tryInit();
        if (listenerCleanup) detectionSettled = true;
      }

      // Capture start_param as soon as it lands — which on Desktop may be
      // AFTER the session was already established above. tryInit() sampled it
      // once and may have stored null; overwrite the moment it appears.
      if (!startParamPublished) {
        const sp = readStartParam();
        if (sp) {
          startParamPublished = true;
          const code = referralCodeFromStartParam(sp);
          if (code) setReferralCode(code);
          setValue((cur) => (cur.startParam === sp ? cur : { ...cur, startParam: sp }));
        }
      }

      // No session and the SDK has had its say: this is a browser. Release the
      // Telegram-gated UI now rather than making it wait out the hard cap.
      if (
        !detectionSettled &&
        sdkSettledAt !== null &&
        now - sdkSettledAt >= GRACE_AFTER_SDK_MS
      ) {
        detectionSettled = true;
        setValue((cur) => (cur.detecting ? { ...cur, detecting: false } : cur));
      }

      if ((listenerCleanup && startParamPublished) || now - startedAt >= HARD_CAP_MS) {
        clearInterval(poll);
        setValue((cur) => (cur.detecting ? { ...cur, detecting: false } : cur));
      }
    }, 300);

    return () => {
      clearInterval(poll);
      listenerCleanup?.();
    };
  }, []);

  return <TwaContext.Provider value={value}>{children}</TwaContext.Provider>;
}

export function useTwa() {
  return useContext(TwaContext);
}
